import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { createContext, useContext } from 'react';
import { useAssignmentActions } from '../hooks/useAssignmentActions';
import { PaymentAssignments } from '../object-actions/types/types';
import { canRemoveCoach, canRemoveParent } from '../utils/assignmentPermissions';

interface AssignmentActionsProps {
    assignment: PaymentAssignments;
    onUpdate: () => void;
    /** When true, treat the current user as the payment author (payer manage page). */
    isPayer?: boolean;
}

type AssignmentActionsContextValue = ReturnType<typeof useAssignmentActions> & {
    assignment: PaymentAssignments;
};

const AssignmentActionsContext = createContext<AssignmentActionsContextValue | null>(null);

function useAssignmentActionsContext() {
    const ctx = useContext(AssignmentActionsContext);
    if (!ctx) {
        throw new Error('Assignment action components must be used within AssignmentRoleActionsProvider');
    }
    return ctx;
}

export const AssignmentRoleActionsProvider: React.FC<AssignmentActionsProps & { children: React.ReactNode }> = ({
    assignment,
    onUpdate,
    isPayer,
    children,
}) => {
    const actions = useAssignmentActions(assignment, onUpdate, isPayer);
    const value: AssignmentActionsContextValue = { ...actions, assignment };

    return (
        <AssignmentActionsContext.Provider value={value}>
            {children}
            {actions.canEdit && (
                <AssignmentRemoveDialog
                    open={actions.removeDialogOpen}
                    itemToRemove={actions.itemToRemove}
                    onClose={() => actions.setRemoveDialogOpen(false)}
                    onConfirm={actions.handleRemoveConfirm}
                />
            )}
        </AssignmentActionsContext.Provider>
    );
};

export const AssignmentEditButton: React.FC = () => {
    const { canEdit, isSubmitted, editTarget, handleEditClick } = useAssignmentActionsContext();

    if (!canEdit) {
        return null;
    }

    if (editTarget.external) {
        return (
            <Tooltip title="Edit Assignment (opens on organization site)">
                <IconButton
                    size="small"
                    component="a"
                    href={editTarget.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={isSubmitted}
                    data-attr="assignment-actions_edit"
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        );
    }

    return (
        <Tooltip title="Edit Assignment">
            <IconButton
                size="small"
                onClick={handleEditClick}
                disabled={isSubmitted}
                data-attr="assignment-actions_edit"
            >
                <EditIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};

interface AssignmentRemoveRoleButtonProps {
    type: 'coach' | 'parent';
    id: number | string;
    name: string;
}

export const AssignmentRemoveRoleButton: React.FC<AssignmentRemoveRoleButtonProps> = ({ type, id, name }) => {
    const { userId, userRole, canEdit, isSubmitted, handleRemoveClick, assignment } = useAssignmentActionsContext();

    const canRemove = type === 'coach'
        ? canRemoveCoach(userId, assignment, id, userRole)
        : canRemoveParent(userId, assignment, id, userRole);

    if (!canEdit || !canRemove) {
        return null;
    }

    return (
        <Tooltip title={`Remove ${name}`}>
            <IconButton
                size="small"
                onClick={() => handleRemoveClick(type, id, name)}
                disabled={isSubmitted}
                color="error"
                data-attr={`assignment-actions_remove-${type}`}
                data-custom-id={String(id)}
                sx={{ flexShrink: 0 }}
            >
                <DeleteIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};

interface AssignmentRemoveDialogProps {
    open: boolean;
    itemToRemove: { type: 'coach' | 'parent'; id: number | string; name: string } | null;
    onClose: () => void;
    onConfirm: () => void;
}

const AssignmentRemoveDialog: React.FC<AssignmentRemoveDialogProps> = ({
    open,
    itemToRemove,
    onClose,
    onConfirm,
}) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
            <Typography>
                Are you sure you want to remove {itemToRemove?.name} from this assignment?
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} data-attr="assignment-actions_remove-dialog-cancel">Cancel</Button>
            <Button onClick={onConfirm} color="error" variant="contained" data-attr="assignment-actions_remove-dialog-confirm">
                Remove
            </Button>
        </DialogActions>
    </Dialog>
);

export default AssignmentRoleActionsProvider;
